import React, { useState, useEffect, useMemo, useId } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useProgressTracker from '../hooks/useProgressTracker.ts';

export type MainStep = 'characters' | 'scenes' | 'videos' | 'final';
export type SubStep = 'fetching' | 'generating' | 'complete';

interface Story {
  id: number;
  child_name: string;
  story_name: string;
  status: string;
  created_at: string;
}

// Memoized presentational components
const StepDot = React.memo<{ status: 'completed' | 'active' | 'current' | 'locked'; icon: string; prefersReducedMotion: boolean }>(({ status, icon, prefersReducedMotion }) => {
  switch (status) {
    case 'completed':
      return (
        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'active':
      return (
        <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
          {prefersReducedMotion ? (
            <div className="w-4 h-4 bg-white rounded-full"></div>
          ) : (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
        </div>
      );
    case 'current':
      return (
        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-lg">
          {icon}
        </div>
      );
    default:
      return (
        <div className="w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center text-slate-400 text-lg">
          {icon}
        </div>
      );
  }
});

const SubstepDot = React.memo<{ status: 'completed' | 'active' | 'pending'; prefersReducedMotion: boolean }>(({ status, prefersReducedMotion }) => {
  return (
    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
      status === 'completed' ? 'bg-green-500' :
      status === 'active' ? 'bg-purple-600' :
      'bg-gray-300'
    }`}>
      {status === 'completed' && (
        <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {status === 'active' && (
        prefersReducedMotion ? (
          <div className="w-2 h-2 bg-white rounded-full"></div>
        ) : (
          <div className="animate-spin rounded-full h-2 w-2 border-b border-white"></div>
        )
      )}
    </div>
  );
});

interface ProgressStepperProps {
  currentStep: 'characters' | 'scenes' | 'videos' | 'final';
  storyId?: string;
  stories?: Story[];
  onStoryChange?: (storyId: string) => void;
}

const ProgressStepper: React.FC<ProgressStepperProps> = ({
  currentStep,
  storyId,
  stories = [],
  onStoryChange
}) => {
  const navigate = useNavigate();
  const progressDescId = useId();

  // Get current page path
  const location = useLocation();
  const currentPage = location.pathname;

  // Always trust the storyId prop from AppLayout (no useParams fallback)
  const activeStoryId = storyId;

  // Use the progress tracker
  const { 
    mainSteps, 
    isMainStepUnlocked, 
    isMainStepActive, 
    isMainStepCompleted,
    shouldShowSubsteps,
    activeSubsteps 
  } = useProgressTracker(activeStoryId, currentPage);

  // Listen for reduced-motion changes at runtime
  const [prefersReducedMotion, setPRM] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setPRM(e.matches);
    setPRM(mql.matches);
    mql.addEventListener?.('change', onChange) ?? mql.addListener(onChange);
    return () => mql.removeEventListener?.('change', onChange) ?? mql.removeListener(onChange);
  }, []);

  const steps = useMemo(() => ([
    {
      id: 'characters' as const,
      label: 'Characters',
      path: activeStoryId ? `/character-builder/${activeStoryId}` : '/characters',
      icon: '👥',
      status: mainSteps.characters.status
    },
    {
      id: 'scenes' as const,
      label: 'Scenes',
      path: activeStoryId ? `/scenes/${activeStoryId}` : '/scenes',
      icon: '🎬',
      status: mainSteps.scenes.status
    },
    {
      id: 'videos' as const,
      label: 'Videos & Audio',
      path: activeStoryId ? `/videos/${activeStoryId}` : '/videos',
      icon: '🎥',
      status: mainSteps.videos.status
    },
    {
      id: 'final' as const,
      label: 'Final Story',
      path: activeStoryId ? `/final-story/${activeStoryId}` : '/final-story',
      icon: '✨',
      status: mainSteps.final.status
    }
  ]), [mainSteps, activeStoryId]);

  const getStepStatus = (stepId: MainStep) => {
    if (stepId === currentStep) return 'current';
    if (isMainStepCompleted(stepId)) return 'completed';
    if (isMainStepActive(stepId)) return 'active';
    return 'locked';
  };

  const canNavigateToStep = (stepId: MainStep) => {
    // Can navigate to current step
    if (stepId === currentStep) return true;
    
    // Can navigate to unlocked steps
    return isMainStepUnlocked(stepId);
  };

  const handleStepClick = (step: typeof steps[0]) => {
    // Don't navigate if step is not clickable
    if (!canNavigateToStep(step.id)) {
      return;
    }

    // Navigate to the step
    navigate(step.path);
  };

  const handleKeyDown = (event: React.KeyboardEvent, step: typeof steps[0]) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleStepClick(step);
    }
  };

  const getStepTextColor = (step: typeof steps[0]) => {
    const status = getStepStatus(step.id);
    
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'active':
        return 'text-purple-600';
      case 'current':
        return 'text-purple-600 font-semibold';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <div className="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700">
      {/* Main Steps */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className={`flex flex-col items-center cursor-pointer transition-all duration-200 ${
                  canNavigateToStep(step.id) ? 'hover:scale-105' : 'cursor-not-allowed'
                }`}
                onClick={() => handleStepClick(step)}
                onKeyDown={(e) => handleKeyDown(e, step)}
                role="button"
                tabIndex={canNavigateToStep(step.id) ? 0 : -1}
                aria-label={`${step.label} - ${getStepStatus(step.id)}`}
                aria-current={getStepStatus(step.id) === 'current' ? 'step' : undefined}
                aria-disabled={!canNavigateToStep(step.id)}
              >
                <div className="relative">
                  <StepDot status={getStepStatus(step.id)} icon={step.icon} prefersReducedMotion={prefersReducedMotion} />
                  
                  {/* Sparkles animation when step gets unlocked */}
                  {isMainStepUnlocked(step.id) && !isMainStepCompleted(step.id) && (
                    <div className="absolute inset-0 pointer-events-none">
                      {prefersReducedMotion ? (
                        <div className="absolute -top-1 -left-1">
                          <span className="text-yellow-400 text-sm">✨</span>
                        </div>
                      ) : (
                        <>
                          <div className="absolute -top-1 -left-1 animate-sparkle-1">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute -top-1 right-0 animate-sparkle-2">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute -bottom-1 -left-1 animate-sparkle-3">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute -bottom-1 -right-1 animate-sparkle-4">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute top-1/2 -left-2 animate-sparkle-5">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute top-1/2 -right-2 animate-sparkle-6">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute -top-2 left-1/2 animate-sparkle-7">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                          <div className="absolute -bottom-2 left-1/2 animate-sparkle-8">
                            <span className="text-yellow-400 text-sm">✨</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <span className={`mt-2 text-sm ${getStepTextColor(step)}`}>
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="flex-1 h-0.5 bg-gray-200 mx-4 relative overflow-hidden" aria-hidden="true">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      isMainStepCompleted(step.id) ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                    style={{ width: isMainStepCompleted(step.id) ? '100%' : '0%' }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Substeps (only visible for active main step) */}
      {shouldShowSubsteps && (
        <div className="bg-slate-800 border-t border-slate-700">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <span id={progressDescId} className="sr-only">
              Green dots indicate completed tasks, purple dots indicate tasks in progress, gray dots indicate pending tasks.
            </span>
            <div 
              className="flex items-center space-x-4"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={activeSubsteps.length}
              aria-valuenow={activeSubsteps.filter(s => s.status === 'completed').length}
              aria-label="Story generation progress"
              aria-describedby={progressDescId}
            >
              {activeSubsteps.map((substep, index) => (
                <React.Fragment key={substep.id}>
                  <div className="flex items-center space-x-2">
                    <SubstepDot status={substep.status} prefersReducedMotion={prefersReducedMotion} />
                    <span className={`text-sm ${
                      substep.status === 'completed' ? 'text-green-600' :
                      substep.status === 'active' ? 'text-purple-600' :
                      'text-slate-400'
                    }`}>
                      {substep.label}
                    </span>
                  </div>
                  
                  {/* Substep connector */}
                  {index < activeSubsteps.length - 1 && (
                    <div className="w-4 h-0.5 bg-gray-300" aria-hidden="true">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          substep.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                        style={{ width: substep.status === 'completed' ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressStepper;